import { getWmsPushPlan } from '@api/mother-order';
import { getStn, pushStnToWms, type Stn } from '@api/warehouse-ops';
import { DataSheet, type DataSheetColumn } from '@components/ui/DataSheet';
import useToaster from '@hooks/toaster/useToaster';
import { Button, Group, Stack, Text } from '@mantine/core';
import { X } from '@phosphor-icons/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { type FC } from 'react';
import { useNavigate } from 'react-router';

import StnStatusBadge from './StnStatusBadge';
import { SOURCE_LABEL, mirroredStatusLabel } from './stnLabels';
import { useStnReceivePath } from './stnReceivePath';
import { isPlatformStn, stnNextStep, useStnActions } from './useStnActions';

type StnItem = NonNullable<Stn['items']>[number];

type Props = { stnId: string; onClose: () => void };

const StnDetailPanel: FC<Props> = ({ stnId, onClose }) => {
    const { showToastSuccess, showToastError } = useToaster();
    // Approve / dispatch / cancel are shared with the Stock Transfers grid's next-step column.
    const { approve, dispatch, cancel, refresh: refreshStn, errText } = useStnActions();
    const navigate = useNavigate();
    // Where this STN is received: a CM site's Receive tab, a store's or warehouse's GRN.
    const { pathFor: receivePathFor, ready: receiveReady } = useStnReceivePath();
    const query = useQuery({
        queryKey: ['stn-panel', stnId],
        queryFn: () => getStn(stnId),
    });
    const { data: wmsPlan } = useQuery(['wms-push-plan'], getWmsPushPlan, { staleTime: 5 * 60_000 });

    const stn = query.data;
    const refresh = () => refreshStn(stnId);
    const pushMutation = useMutation({
        mutationFn: () => pushStnToWms(stnId),
        onSuccess: (updated) => { showToastSuccess({ title: `Gate pass ${updated.wmsGatePassCode ?? updated.code} created in WMS` }); refresh(); },
        onError: (e: any) => showToastError({ title: 'WMS push failed', subtitle: errText(e) }),
    });

    // Only an STN raised on the platform, leaving one of OUR warehouses, is ours to act on.
    const isPlatform = !!stn && isPlatformStn(stn);
    // The same next step as the Stock Transfers grid: Approve a draft, Dispatch an approved note
    // (not one raised from a sale order, which is dispatched on the order), Receive only where the
    // STN has a page it is received on. A channel, customer or distributor receives on their side.
    const receivePath = stn && receiveReady ? receivePathFor(stn) : null;
    const nextStep = stn ? stnNextStep(stn, !!receivePath) : null;
    const canApprove = nextStep === 'APPROVE';
    const canDispatch = nextStep === 'DISPATCH';
    const canCancel = isPlatform && (stn.status === 'DRAFT' || stn.status === 'APPROVED');
    const canReceive = nextStep === 'RECEIVE' && !!receivePath;
    const canPush = isPlatform && !!wmsPlan?.stn && !stn.wmsGatePassCode && stn.status !== 'CANCELLED';
    // Mirrored from a source system (Amazon FBA, a WMS gate pass) rather than raised here.
    const mirrored = Boolean(stn?.source && stn.source !== 'PLATFORM');

    const pushHint = stn?.toNodeKind === 'INTERNAL'
        ? `Will create a STOCK_TRANSFER gate pass to ${stn.toNodeName}`
        : `Will create a NON_RETURNABLE gate pass to ${stn?.toNodeName ?? 'the destination'}`;

    const itemColumns: DataSheetColumn<StnItem>[] = [
        {
            // SKU over its name, the same identity cell as the Inventory grids.
            key: 'itemId',
            header: 'SKU',
            width: 340,
            sticky: true,
            captureDetailHeader: 'SKU Description',
            valueAccessor: (r) => r.itemId,
            render: (_, r) => (
                <span className="flex min-w-0 flex-col leading-tight">
                    <span className="break-all font-mono text-xs text-slate-600">{r.itemId}</span>
                    {r.itemName && (
                        <Text size="11px" c="dimmed" truncate>
                            {r.itemName}
                        </Text>
                    )}
                </span>
            ),
        },
        {
            key: 'plannedQty', header: 'Planned', width: 110, align: 'right',
            render: (_, r) => r.plannedQty,
        },
        {
            key: 'dispatchedQty', header: 'Dispatched', width: 110, align: 'right',
            // A line the source dropped (Amazon deleted it from the shipment) sits at 0 with the
            // figure it carried kept beside it, so the audit trail stays readable.
            render: (_, r) =>
                r.removedQty != null ? (
                    <Text size="xs" c="dimmed">
                        removed, was {r.removedQty}
                    </Text>
                ) : (
                    r.dispatchedQty ?? '—'
                ),
        },
        {
            key: 'receivedQty', header: 'Received', width: 110, align: 'right',
            render: (_, r) => r.receivedQty ?? '—',
        },
    ];

    return (
        <div className="fixed inset-0 z-30 flex items-stretch justify-end bg-slate-900/10 backdrop-blur-sm">
            <div className="relative flex h-full w-full max-w-3xl flex-col border-l border-slate-200 bg-white shadow-2xl">
                {/* Header */}
                <div className="flex items-start justify-between border-b border-slate-200 px-6 py-4">
                    <div>
                        <Group gap="sm">
                            <Text size="lg" fw={700}>{stn?.code ?? '…'}</Text>
                            {stn && (
                                <StnStatusBadge status={stn.status} label={mirroredStatusLabel(stn)} />
                            )}
                        </Group>
                        {stn && (
                            <Text size="xs" c="dimmed" mt={2}>
                                {stn.fromNodeName ?? stn.fromNodeId ?? '—'} → {stn.toNodeName ?? stn.toNodeId}
                                {stn.toNodeKind && stn.toNodeKind !== 'INTERNAL' ? ` (${stn.toNodeKind.toLowerCase()})` : ''}
                                {' · '}{stn.itemType === 'product' ? 'Finished Good' : 'Raw / PM'}
                                {' · '}{SOURCE_LABEL[stn.source ?? ''] ?? stn.source}
                                {stn.wmsGatePassCode ? ` · WMS gate pass ${stn.wmsGatePassCode}` : ''}
                            </Text>
                        )}
                    </div>
                    <Group gap="xs" wrap="nowrap">
                        {canCancel && (
                            <Button size="xs" variant="subtle" color="red" loading={cancel.isPending} onClick={() => cancel.mutate(stnId)}>
                                Cancel
                            </Button>
                        )}
                        {canPush && (
                            <Button size="xs" variant="default" loading={pushMutation.isPending} title={pushHint} onClick={() => pushMutation.mutate()}>
                                Push to WMS
                            </Button>
                        )}
                        {canApprove && (
                            <Button size="xs" variant="light" color="violet" loading={approve.isPending} onClick={() => approve.mutate(stnId)}>
                                Approve
                            </Button>
                        )}
                        {canDispatch && (
                            <Button size="xs" loading={dispatch.isPending} onClick={() => dispatch.mutate(stnId)}>
                                Dispatch
                            </Button>
                        )}
                        {canReceive && (
                            <Button size="xs" color="green" onClick={() => receivePath && void navigate(receivePath)}>
                                Mark received
                            </Button>
                        )}
                        <button onClick={onClose} className="rounded-full p-2 text-slate-500 hover:bg-slate-100">
                            <X size={18} />
                        </button>
                    </Group>
                </div>

                {/* Body */}
                {stn && (
                    <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-6 py-5">
                        {/* Meta */}
                        <div className="grid grid-cols-2 gap-3 rounded-xl bg-slate-50 p-4 text-sm">
                            {/*
                              A shipment mirrored from a source system (Amazon FBA, a WMS gate pass)
                              shows the source's own timeline: "Created" is when the brand created it
                              THERE (approved_at), not when Reparo first saw it, and a shipment whose
                              source gave no creation time shows no Created cell rather than ours.
                              A note raised in Reparo keeps the platform words below.
                            */}
                            {mirrored ? (
                                // The four cells always render so the block reads as a timeline
                                // even before the source has reported a date; a dash is "not yet".
                                <>
                                    <Stack gap={2}>
                                        <Text size="xs" c="dimmed">Created</Text>
                                        <Text size="sm">{stn.approvedAt ? new Date(stn.approvedAt).toLocaleString() : '—'}</Text>
                                    </Stack>
                                    <Stack gap={2}>
                                        <Text size="xs" c="dimmed">Ship by</Text>
                                        <Text size="sm">{stn.plannedDispatchDate ? new Date(stn.plannedDispatchDate).toLocaleDateString() : '—'}</Text>
                                    </Stack>
                                    <Stack gap={2}>
                                        <Text size="xs" c="dimmed">Delivery appointment</Text>
                                        <Text size="sm">
                                            {stn.expectedArrivalDate ? (
                                                <>
                                                    {new Date(stn.expectedArrivalDate).toLocaleDateString()}
                                                    <Text span size="xs" c="dimmed"> · {stn.receivedAt ? 'arrived' : 'booked'}</Text>
                                                </>
                                            ) : (
                                                <Text span c="dimmed">not booked</Text>
                                            )}
                                        </Text>
                                    </Stack>
                                    <Stack gap={2}>
                                        <Text size="xs" c="dimmed">Received</Text>
                                        <Text size="sm">{stn.receivedAt ? new Date(stn.receivedAt).toLocaleDateString() : '—'}</Text>
                                    </Stack>
                                </>
                            ) : (
                                <>
                                    <Stack gap={2}>
                                        <Text size="xs" c="dimmed">Created by</Text>
                                        <Text size="sm">{stn.createdBy ?? '—'}</Text>
                                    </Stack>
                                    <Stack gap={2}>
                                        <Text size="xs" c="dimmed">Created</Text>
                                        <Text size="sm">{new Date(stn.createdAt).toLocaleDateString()}</Text>
                                    </Stack>
                                    {stn.approvedAt && (
                                        <Stack gap={2}>
                                            <Text size="xs" c="dimmed">Approved</Text>
                                            <Text size="sm">{new Date(stn.approvedAt).toLocaleString()}</Text>
                                        </Stack>
                                    )}
                                </>
                            )}
                            {!mirrored && stn.plannedDispatchDate && !stn.dispatchedAt && (
                                <Stack gap={2}>
                                    <Text size="xs" c="dimmed">Dispatch on</Text>
                                    <Text size="sm">{new Date(stn.plannedDispatchDate).toLocaleDateString()}</Text>
                                </Stack>
                            )}
                            {stn.dispatchedAt && (
                                <Stack gap={2}>
                                    <Text size="xs" c="dimmed">Dispatched</Text>
                                    <Text size="sm">{new Date(stn.dispatchedAt).toLocaleString()}</Text>
                                </Stack>
                            )}
                            {!mirrored && stn.expectedArrivalDate && (
                                <Stack gap={2}>
                                    <Text size="xs" c="dimmed">Expected arrival</Text>
                                    <Text size="sm">{new Date(stn.expectedArrivalDate).toLocaleDateString()}</Text>
                                </Stack>
                            )}
                            {!mirrored && stn.receivedAt && (
                                <Stack gap={2}>
                                    <Text size="xs" c="dimmed">Received</Text>
                                    <Text size="sm">{new Date(stn.receivedAt).toLocaleString()}</Text>
                                </Stack>
                            )}
                            {stn.workOrderId && (
                                <Stack gap={2}>
                                    <Text size="xs" c="dimmed">Work Order</Text>
                                    <Text size="sm">{stn.workOrderId}</Text>
                                </Stack>
                            )}
                        </div>

                        {stn.notes && (
                            <Stack gap={2}>
                                <Text size="xs" c="dimmed">Notes</Text>
                                <Text size="sm">{stn.notes}</Text>
                            </Stack>
                        )}

                        {/* Items */}
                        <DataSheet<StnItem>
                            title="Items"
                            data={stn.items ?? []}
                            columns={itemColumns}
                            getRowKey={(r) => r.id}
                            maxHeight="auto"
                            hideHeaderBar={false}
                        />

                        {stn.status === 'RECEIVED' && (
                            <Text size="sm" c="dimmed">
                                STN received. Create a GRN at the destination warehouse to process stock.
                            </Text>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default StnDetailPanel;
